/**
 * Apple Pay / Google Pay Payment Component
 * Uses Stripe Payment Request Button API
 * 
 * Automatically detects available payment methods:
 * - Apple Pay (Safari on iOS/macOS)
 * - Google Pay (Chrome on Android/Desktop)
 * - Microsoft Pay (Edge)
 * - Browser saved cards
 */

import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentRequestButtonElement, useStripe } from '@stripe/react-stripe-js';
import { AppleLogo, GoogleLogo, Wallet, Warning, Check } from '@phosphor-icons/react';
import { toast } from 'sonner';

const STRIPE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

// Only load Stripe if key is configured
const stripePromise = STRIPE_KEY ? loadStripe(STRIPE_KEY) : null;

/**
 * Inner component that uses Stripe hooks
 */
const WalletPaymentInner = ({ 
  amount, 
  currency = 'gbp', 
  label = 'habitué Top-up',
  onSuccess,
  onError,
  language = 'en'
}) => {
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [canMakePayment, setCanMakePayment] = useState(null);
  const [walletType, setWalletType] = useState(null);
  const [processing, setProcessing] = useState(false);

  const t = {
    fr: {
      loading: 'Chargement...',
      notAvailable: 'Apple Pay / Google Pay non disponible',
      notAvailableDesc: 'Utilisez une carte bancaire ou configurez un portefeuille mobile sur cet appareil.',
      browserNotSupported: 'Votre navigateur ne supporte pas les paiements mobiles',
      useCard: 'Utiliser une carte',
      processing: 'Traitement...',
      payWith: 'Payer avec',
      orPayWith: 'ou payer avec',
      configureFirst: 'Configurez d\'abord Apple Pay ou Google Pay sur cet appareil',
    },
    en: {
      loading: 'Loading...',
      notAvailable: 'Apple Pay / Google Pay not available',
      notAvailableDesc: 'Use a credit card or set up a mobile wallet on this device.',
      browserNotSupported: 'Your browser doesn\'t support mobile payments',
      useCard: 'Use a card',
      processing: 'Processing...',
      payWith: 'Pay with',
      orPayWith: 'or pay with',
      configureFirst: 'First configure Apple Pay or Google Pay on this device',
    }
  }[language];

  useEffect(() => {
    if (!stripe || !amount) return;

    const pr = stripe.paymentRequest({
      country: 'GB',
      currency: currency.toLowerCase(),
      total: {
        label,
        amount: Math.round(amount * 100), // Stripe uses cents
      },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    // Check if Payment Request is available
    pr.canMakePayment().then(result => {
      if (result) {
        setPaymentRequest(pr);
        setCanMakePayment(true);
        // Detect wallet type
        if (result.applePay) {
          setWalletType('apple');
        } else if (result.googlePay) {
          setWalletType('google');
        } else {
          setWalletType('browser');
        }
      } else {
        setCanMakePayment(false);
      }
    });

    // Handle payment method
    pr.on('paymentmethod', async (ev) => {
      setProcessing(true);
      
      try {
        // In production, send to backend to create PaymentIntent
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/wallet-payment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            amount,
            currency,
            payment_method_id: ev.paymentMethod.id,
            wallet_type: walletType,
          }),
        });

        const { client_secret, error: backendError } = await response.json();

        if (backendError) {
          ev.complete('fail');
          onError?.(new Error(backendError));
          return;
        }

        // Confirm the payment
        const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
          client_secret,
          { payment_method: ev.paymentMethod.id },
          { handleActions: false }
        );

        if (stripeError) {
          ev.complete('fail');
          onError?.(stripeError);
        } else {
          ev.complete('success');
          if (paymentIntent.status === 'requires_action') {
            // Handle 3D Secure
            const { error } = await stripe.confirmCardPayment(client_secret);
            if (error) {
              onError?.(error);
            } else {
              onSuccess?.(paymentIntent);
            }
          } else {
            onSuccess?.(paymentIntent);
          }
        }
      } catch (err) {
        ev.complete('fail');
        onError?.(err);
      } finally {
        setProcessing(false);
      }
    });

    pr.on('cancel', () => {
      setProcessing(false);
    });

  }, [stripe, amount, currency, label, onSuccess, onError, walletType]);

  // Loading state
  if (canMakePayment === null) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="w-5 h-5 border-2 border-[#C3A365] border-t-transparent rounded-full animate-spin mr-2" />
        <span className="text-[#8BA89A] text-sm">{t.loading}</span>
      </div>
    );
  }

  // Wallet not available
  if (!canMakePayment) {
    return (
      <div className="bg-[#1E2F26]/50 border border-[#283D32] rounded-xl p-4 text-center">
        <div className="flex items-center justify-center gap-3 mb-2">
          <AppleLogo size={20} className="text-[#8BA89A]" />
          <GoogleLogo size={20} className="text-[#8BA89A]" />
        </div>
        <p className="text-[#8BA89A] text-sm">{t.notAvailable}</p>
        <p className="text-[#6B7B73] text-xs mt-1">{t.notAvailableDesc}</p>
      </div>
    );
  }

  // Wallet available - show button
  return (
    <div className="space-y-3">
      {/* Wallet Type Indicator */}
      <div className="flex items-center justify-center gap-2 text-[#8BA89A] text-xs">
        {walletType === 'apple' && (
          <>
            <AppleLogo size={16} weight="fill" />
            <span>{t.payWith} Apple Pay</span>
          </>
        )}
        {walletType === 'google' && (
          <>
            <GoogleLogo size={16} weight="fill" />
            <span>{t.payWith} Google Pay</span>
          </>
        )}
        {walletType === 'browser' && (
          <>
            <Wallet size={16} />
            <span>{t.payWith} Browser Wallet</span>
          </>
        )}
      </div>

      {/* Payment Request Button */}
      <div className="payment-request-button-container">
        {paymentRequest && (
          <PaymentRequestButtonElement
            options={{
              paymentRequest,
              style: {
                paymentRequestButton: {
                  type: 'default',
                  theme: 'dark',
                  height: '48px',
                },
              },
            }}
          />
        )}
      </div>

      {processing && (
        <div className="flex items-center justify-center py-2">
          <div className="w-4 h-4 border-2 border-[#C3A365] border-t-transparent rounded-full animate-spin mr-2" />
          <span className="text-[#C3A365] text-sm">{t.processing}</span>
        </div>
      )}
    </div>
  );
};

/**
 * Demo mode component when Stripe is not configured
 */
const WalletPaymentDemo = ({ amount, language = 'en' }) => {
  const [demoPayment, setDemoPayment] = useState(false);

  const t = {
    fr: {
      demoMode: 'Mode démo',
      demoDesc: 'Configurez REACT_APP_STRIPE_PUBLISHABLE_KEY pour activer Apple Pay / Google Pay',
      simulatePay: 'Simuler le paiement',
      processing: 'Traitement...',
      success: 'Paiement simulé!',
    },
    en: {
      demoMode: 'Demo mode',
      demoDesc: 'Configure REACT_APP_STRIPE_PUBLISHABLE_KEY to enable Apple Pay / Google Pay',
      simulatePay: 'Simulate payment',
      processing: 'Processing...',
      success: 'Payment simulated!',
    }
  }[language];

  const handleDemoPayment = () => {
    setDemoPayment(true);
    setTimeout(() => {
      toast.success(t.success);
      setDemoPayment(false);
    }, 2000);
  };

  return (
    <div className="space-y-3">
      {/* Demo wallet buttons */}
      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={handleDemoPayment}
          disabled={demoPayment}
          className="flex items-center justify-center gap-2 bg-black text-white py-3 rounded-xl font-medium hover:bg-gray-900 transition-colors disabled:opacity-50"
          data-testid="apple-pay-demo"
        >
          <AppleLogo size={20} weight="fill" />
          <span>Pay</span>
        </button>
        <button
          onClick={handleDemoPayment}
          disabled={demoPayment}
          className="flex items-center justify-center gap-2 bg-white text-black py-3 rounded-xl font-medium hover:bg-gray-100 transition-colors border border-gray-200 disabled:opacity-50"
          data-testid="google-pay-demo"
        >
          <GoogleLogo size={20} weight="fill" />
          <span>Pay</span>
        </button>
      </div>

      {demoPayment && (
        <div className="flex items-center justify-center py-2">
          <div className="w-4 h-4 border-2 border-[#C3A365] border-t-transparent rounded-full animate-spin mr-2" />
          <span className="text-[#C3A365] text-sm">{t.processing}</span>
        </div>
      )}

      {/* Demo mode notice */}
      <div className="flex items-start gap-2 p-3 bg-[#C3A365]/10 border border-[#C3A365]/30 rounded-lg">
        <Warning size={16} className="text-[#C3A365] mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-[#C3A365] text-xs font-medium">{t.demoMode}</p>
          <p className="text-[#8BA89A] text-xs mt-0.5">{t.demoDesc}</p>
        </div>
      </div>
    </div>
  );
};

/**
 * Main export component
 */
const WalletPayment = ({ amount, currency = 'gbp', label, onSuccess, onError, language = 'en' }) => {
  // If Stripe is not configured, show demo mode
  if (!stripePromise) {
    return <WalletPaymentDemo amount={amount} language={language} />;
  }

  return (
    <Elements stripe={stripePromise}>
      <WalletPaymentInner
        amount={amount}
        currency={currency}
        label={label}
        onSuccess={onSuccess}
        onError={onError}
        language={language}
      />
    </Elements>
  );
};

export default WalletPayment;
